import * as R from 'ramda'
import {
  CreateWorkspace,
  GetWorkspaces,
  reqCreateWorkspace,
  reqGetWorkspaces,
  ReqGetWorkspacesPayload,
  reqSearchWorkspaces,
  SearchWorkspaces,
  reqUpdateWorkspace,
  UpdateWorkspace,
  DeleteWorkspace,
  reqDeleteWorkspace,
  ReqGetOwnerWorkspacesPayload,
  GetOwnerWorkspaces,
  reqGetOwnerWorkspaces,
} from '@weblite/ts-action/workspace'
import { EventAddNewRoomPayload, Room } from '@weblite/ts-action/room'
import { getRooms, updateRoom } from '$logic/room'
import { workspaceStorage } from '$logic/workspace/storage'
import { DEFAULT_WORKSPACE_ID } from '$logic/workspace/constant'
import { WORKSPACE_ENABLED } from '$config/env'
import { request } from '$setup/ws'
import { dispatchSetWorkspace } from '$comp/main/root.action'
import {
  showGettingOwnerWorkspaceFailedSnackbar,
  showHallsMovedToWorkspaceSuccessfullySnackbar,
  showHallsRemovedFromWorkspaceSuccessfullySnackbar,
  showWorkspaceCreatedSuccessfully,
  showWorkspaceDeletedSuccessfully,
  showWorkspaceUpdatedSuccessfully,
} from '$comp/snackbars'
import {
  MoveHallsToWorkspace,
  reqMoveHallsToWorkspace,
} from '@weblite/ts-action/hall'

export const getOwnerWorkspaces = async ({
  after,
}: ReqGetOwnerWorkspacesPayload) => {
  const res = await request<GetOwnerWorkspaces>(
    reqGetOwnerWorkspaces({ after }),
  )
  if (res.error) {
    showGettingOwnerWorkspaceFailedSnackbar()
    throw new Error()
  }
  return res.payload?.workspaces
}

export const getWorkspaces = ({
  workspaceIds,
  after,
}: ReqGetWorkspacesPayload) => {
  return request<GetWorkspaces>(
    reqGetWorkspaces({
      workspaceIds: workspaceIds as string[],
      after,
    }),
  )
}

export const searchWorkspaces = (query: string | undefined) => {
  return request<SearchWorkspaces>(reqSearchWorkspaces({ query }))
}

export async function syncWorkspaces() {
  if (!WORKSPACE_ENABLED) return
  const rooms = getRooms()
  const workspaceIds = R.pluck('workspaceId', rooms).filter(Boolean) as string[]
  const res = await getWorkspaces({ workspaceIds })

  if (res.error?.message) {
    return console.error(res.error.message)
  }

  if (!res.payload?.workspaces?.length) return
  await workspaceStorage.add(res.payload.workspaces)
}

export const resetWorkspace = () => {
  dispatchSetWorkspace(DEFAULT_WORKSPACE_ID)
}

export const addWorkspace = async (workspaceId: string) => {
  const res = await getWorkspaces({ workspaceIds: [workspaceId] })
  if (res.error) {
    console.error(res.error)
    return
  }
  if (!res.payload.workspaces?.length) return
  workspaceStorage.add(res.payload.workspaces)
}

export async function deleteWorkspaceIfHasNoOtherRooms(deletedRoom: Room) {
  if (!deletedRoom) return

  const { workspaceId } = deletedRoom
  if (!workspaceId) return

  const rooms = getRooms()
  const workspaceHasOtherRooms = rooms.some(
    room => room.workspaceId === workspaceId && room.id !== deletedRoom.id,
  )

  if (!workspaceHasOtherRooms) {
    workspaceStorage.del(workspaceId)
    resetWorkspace()
  }
}

export async function addWorkspaceOnJoinHall(
  addedRoom: EventAddNewRoomPayload,
) {
  if (!addedRoom || !addedRoom.workspaceId) return

  const { workspaceId } = addedRoom
  if (!workspaceId) return

  const rooms = getRooms()
  const workspaceHasAlreadyBeenAdded = rooms.some(
    room => room.workspaceId === workspaceId && room.id !== addedRoom.id,
  )

  if (!workspaceHasAlreadyBeenAdded) {
    const res = await getWorkspaces({ workspaceIds: [workspaceId] })
    if (res.error?.message) {
      return console.error(res.error.message)
    }
    if (res.payload?.workspaces?.length) {
      workspaceStorage.add(res.payload.workspaces)
      dispatchSetWorkspace(res.payload.workspaces[0].id)
    }
  } else dispatchSetWorkspace(workspaceId)
}

export const createWorkspace = async ({
  name,
  shortName,
  description,
  color,
  icon,
}: {
  name: string
  shortName?: string
  description?: string
  color: string
  icon: string
}) => {
  const res = await request<CreateWorkspace>(
    reqCreateWorkspace({ name, shortName, description, color, icon }),
  )
  if (res.error) {
    console.warn(res.error)
    return
  }
  await workspaceStorage.add([res.payload.workspace])
  openWorkspace(res.payload.workspace.id)
  showWorkspaceCreatedSuccessfully()
  return res.payload.workspace.id
}

export const updateWorkspace = async ({
  name,
  shortName,
  description,
  color,
  icon,
  workspaceId,
}: {
  name: string
  shortName?: string
  description?: string
  color: string
  icon: string
  workspaceId: string
}) => {
  const res = await request<UpdateWorkspace>(
    reqUpdateWorkspace({
      workspace: {
        id: workspaceId,
        name,
        shortName,
        description,
        color,
        icon,
      },
    }),
  )
  if (res.error) {
    console.warn(res.error)
    return
  }

  await workspaceStorage.add([res.payload.workspace])
  showWorkspaceUpdatedSuccessfully()
  return res.payload.workspace.id
}

export const deleteWorkspace = async (workspaceId: string) => {
  const res = await request<DeleteWorkspace>(
    reqDeleteWorkspace({ id: workspaceId }),
  )
  if (res.error) {
    console.warn(res.error)
    return
  }
  showWorkspaceDeletedSuccessfully()
  workspaceStorage.del(workspaceId)
  return res.payload.workspace.id
}

export const openWorkspace = (workspaceId: string) => {
  dispatchSetWorkspace(workspaceId)
}

export const moveHallsToWorkspace = async (
  workspaceId: string | null,
  hallIds: string[],
) => {
  const res = await request<MoveHallsToWorkspace>(
    reqMoveHallsToWorkspace({ workspaceId, hallIds }),
  )
  if (res.error) {
    console.warn(res.error)
    return
  }
  res.payload.rooms.forEach(async ({ id, updatedAt }) =>
    updateRoom(id, { workspaceId, updatedAt }),
  )
  if (workspaceId) {
    showHallsMovedToWorkspaceSuccessfullySnackbar()
    openWorkspace(workspaceId)
  } else {
    showHallsRemovedFromWorkspaceSuccessfullySnackbar()
    resetWorkspace
  }
  return
}
